import type { Space } from '@cocoonspace/sdk-js/types/space'
import { memo } from 'react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useLingui } from '@lingui/react/macro'
import { motion } from 'motion/react'

import { Stepper } from '~/components/stepper'
import { useAuth } from '~/providers/auth-provider'
import { StepDay } from './step-day/step-day'
import { StepIntro } from './step-intro'
import { StepMultidates } from './step-multidates/step-multidates'
import { StepTime } from './step-time/step-time'
import {
	BOOKING_STEPS,
	useBookingActiveStep,
} from './use-booking-active-step.hook'
import { useBookingOptions } from './use-booking-options.hook'

export const BOOKING_MAX_DATE = dateTimeUtils()
	.add(1, 'year')
	.format('YYYY-MM-DD')

export const BookingStepper = memo(({ space }: { space: Space }) => {
	const { t } = useLingui()
	const { isAuth, openRegister, setAuthErrorMsg } = useAuth()

	const [activeStep, setActiveStep] = useBookingActiveStep()
	const [bookingOption, setBookingOption] = useBookingOptions()

	return (
		<Stepper
			defaultActiveStep={activeStep}
			onStepChange={(step) => setActiveStep(step)}
			render={({ framerConfig, activeStep, goToStep }) => (
				<>
					{activeStep === 'start' && (
						<motion.div
							key='start'
							{...framerConfig}
						>
							<StepIntro
								onChange={(bookingMode) => {
									if (!isAuth) {
										setAuthErrorMsg(
											t`Vous devez être connecté(e) pour réserver un Cocoon`,
										)
										openRegister()
										return
									}

									const nextStep =
										bookingMode === 'hourly' ? 'daySelect' : 'multiDaysSelect'

									goToStep(nextStep)

									window.scrollTo({ behavior: 'smooth', top: 501 })
								}}
								space={space}
								value='hourly'
							/>
						</motion.div>
					)}

					{activeStep === 'daySelect' && (
						<motion.div
							key='daySelect'
							{...framerConfig}
						>
							<StepDay
								onChange={(day) => {
									setBookingOption({ day: [day] })
									goToStep('timeSelect')
								}}
								onClickBack={() => {
									setBookingOption(null)
									goToStep('start')
								}}
								space={space}
								value={bookingOption.day?.[0]}
							/>
						</motion.div>
					)}

					{activeStep === 'timeSelect' && (
						<motion.div
							key='timeSelect'
							{...framerConfig}
						>
							<StepTime
								maxDate={BOOKING_MAX_DATE}
								mode='create'
								onChange={(value) => setBookingOption(value)}
								onClickBack={() => goToStep('daySelect')}
								space={space}
								type='meeting'
								value={bookingOption}
							/>
						</motion.div>
					)}

					{activeStep === 'multiDaysSelect' && (
						<motion.div
							key='multiDaysSelect'
							{...framerConfig}
						>
							<StepMultidates
								onClickBack={() => {
									setBookingOption({ day: null })
									goToStep('start')
								}}
								space={space}
								value={bookingOption.day || []}
							/>
						</motion.div>
					)}
				</>
			)}
			steps={BOOKING_STEPS}
		/>
	)
})

BookingStepper.displayName = 'BookingStepper'
